import * as juration from 'juration';
import { ChronoUnit, LocalDate, LocalDateTime, LocalTime } from '@js-joda/core';
import { dayjs, isAtOrBefore } from '#gp/app-common/util/dayjs';

export enum YearOptions {
    AlwaysShowYear, ShowYearIfNeeded, NeverShowYear
}

export interface DateOptions {
    // if not specfied, defaults to ShowYearIfNeeded
    yearOptions?: YearOptions;
    relativeTo: dayjs.Dayjs | null;
    /** Keep the time beyond the near horizon - see formatEasyToReadDateTime. */
    alwaysShowTime?: boolean;
    /** Keep the weekday even when the year shows - see formatEasyToReadDateTime. */
    alwaysShowWeekday?: boolean;
}

export interface IDateFormatter {
    formatDateTime(when: dayjs.Dayjs): string;
    formatDateTime(when: dayjs.Dayjs, options: DateOptions): string;
    formatDate(when: dayjs.Dayjs): string;
    formatDate(when: dayjs.Dayjs, options: DateOptions): string;
    formatDateShort(when: dayjs.Dayjs): string;
    formatTime(when: dayjs.Dayjs): string;
    formatDurationToDaysAndHoursRelativeToNothing(hours: number): string;
    formatDurationRelativeToStartDate(start: dayjs.Dayjs, end: dayjs.Dayjs, durationFormatStyle: DurationFormatStyle): string;
    formatHours(hours: number, durationFormatStyle: DurationFormatStyle): string;

    formatLocalDateTime(when: LocalDateTime, options?: DateOptions): string;
    formatLocalDate(when: LocalDate, options?: DateOptions): string;
    formatLocalTime(when: LocalTime, options?: DateOptions): string;
}


const intlNumberFormatTwoDecimals = Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const intlNumberFormatOneDecimals = Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
});

// "1 hour ago", "2 hours ago" — the count decides the plural.
function countAgo(count: number, unit: string): string {
    return count + " " + (count === 1 ? unit : unit + "s") + " ago";
}

export function formatRelativeTimeInPast(utc: dayjs.Dayjs | null, showTime: boolean) {
    if (utc) {
        var editedUtc = utc;
        var nowUtc = dayjs().utc();

        var minutesAgo = (nowUtc.diff(editedUtc, "minutes"));
        var hoursAgo = (nowUtc.diff(editedUtc, "hours"));
        var daysAgo = (nowUtc.diff(editedUtc, "days"));

        var editedLocal = dayjs().add(-minutesAgo, 'minutes');

        if (utc.isAfter(nowUtc)) {
            return "In the future";
        } else if (daysAgo > 3) {
            // Older saves: the product's standard easy-read date (weekday XOR
            // year, 90-day near horizon). Nobody needs the weekday or the time
            // of day for something saved months or years ago, and the old
            // verbose format ("Friday, November 4, 2016 1:37 AM") ate two
            // lines of a plan card.
            return formatEasyToReadDate(editedLocal, dayjs().year());
        } else if (daysAgo > 0) {
            return showTime
                ? countAgo(daysAgo, "day") + " at " + editedLocal.format("LT")
                : countAgo(daysAgo, "day");
        } else if (minutesAgo >= 60) {
            return showTime
                ? countAgo(hoursAgo, "hour") + " at " + editedLocal.format("LT")
                : countAgo(hoursAgo, "hour");
        } else if (minutesAgo > 0) {
            return countAgo(minutesAgo, "minute");
        } else {
            return "Just now";
        }
    } else {
        return null;
    }
}

/**
 * Rolling near/far horizon for date rendering (the two-rule model):
 * weekday and time-of-day are OPERATIONAL detail, shown only for dates
 * within this many days of today; the year is DISAMBIGUATION, shown only
 * for far dates in a different calendar year. Every rendered date is
 * either fully operational ("Mon 3 Aug 8am") or fully orientational
 * ("7 Jan 2027"), never the busy both ("Mon 3 Jan 2027 8am"). This
 * replaces the old current-calendar-year test, whose near/far flipped at
 * an arbitrary boundary (a January task read as "far" in late December).
 * Deliberately a baked constant, not a setting.
 * MIRRORED in C# (GamePlanReporting/Formatting.cs NearDateHorizonDays)
 * so the exported/printed whiteboard matches the live one - keep in sync.
 */
export const NEAR_DATE_HORIZON_DAYS = 90;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Signed whole-calendar-day difference from `from` to `to`, ignoring
 * time-of-day (positive = `to` is later). For plans scheduled in days,
 * where variance must be derived from the dates the user can actually
 * SEE rather than from elapsed time: due Nov 1 vs finished Nov 2 is
 * "1 day", never "1 day 8 hours" (which reads as ~1.33 days and cannot
 * be reconciled against two dates that differ by one).
 */
export function calendarDaysBetween(from: dayjs.Dayjs, to: dayjs.Dayjs): number {
    const a = dayjs(from.valueOf()).utcOffset(from.utcOffset()).startOf('day').valueOf();
    const b = dayjs(to.valueOf()).utcOffset(to.utcOffset()).startOf('day').valueOf();
    return Math.round((b - a) / MS_PER_DAY);
}

/**
 * "1 day" / "2 days" for a whole-day count. Callers add the direction
 * ("late" / "early"); zero is the caller's business (it means the dates
 * on screen are identical, i.e. on time in a day-granular plan).
 */
export function formatWholeDays(days: number): string {
    const n = Math.abs(days);
    return `${n} day${n === 1 ? '' : 's'}`;
}

/**
 * Near/far and "current year" are both evaluated in the DATE'S own zone,
 * against calendar days rather than raw instants: every time on a given
 * day shares one style (no mixed formats for tasks on the same day, no
 * style flip mid-viewing-day - only at midnight), and the same instant
 * classifies identically in the browser and the C# export server
 * regardless of their local zones (New Year straddle).
 */
function nearAndNowYear(when: dayjs.Dayjs, now: dayjs.Dayjs): { near: boolean; nowYear: number } {
    const nowInWhenZone = dayjs(now.valueOf()).utcOffset(when.utcOffset());
    const dayDiff = Math.round(Math.abs(
        when.startOf('day').valueOf() - nowInWhenZone.startOf('day').valueOf()) / MS_PER_DAY);
    return { near: dayDiff <= NEAR_DATE_HORIZON_DAYS, nowYear: nowInWhenZone.year() };
}

export function formatEasyToReadDateTime(when: dayjs.Dayjs, thisYear: number, options?: {
    relativeTo?: dayjs.Dayjs | null,
    hideYear?: boolean,
    showZeroMinutes?: boolean,
    /**
     * Keep the time even beyond the near horizon. For surfaces where the
     * time is SEMANTIC, not scheduling detail: work-session start/end
     * pairs (a timesheet row's columns would otherwise render identical),
     * due-vs-finish comparisons ("2 hrs late" needs the times that
     * differ), recorded actuals and audit timestamps, and same-day Gantt
     * bar labels whose only differentiator is the time.
     */
    alwaysShowTime?: boolean,
    /**
     * Keep the weekday even when the year is shown, breaking the usual
     * weekday-XOR-year rule. For ANCHOR dates that a reader orients the
     * whole plan around - the Summary panel's Plan Start / Plan End -
     * where the weekday is genuinely useful ("it starts tomorrow, a
     * Monday") and there is room for both.
     */
    alwaysShowWeekday?: boolean,
    /** Injectable "today" for deterministic specs; defaults to now. */
    now?: dayjs.Dayjs,
}): string {

    let result: string;

    options = options || {};
    const now = options.now || dayjs();

    if (options.relativeTo && options.relativeTo.year() === when.year() && options.relativeTo.month() === when.month() && options.relativeTo.date() === when.date()) {
        result = when.format("h:mm a");
    } else {
        // Strict weekday XOR year: the weekday shows exactly when the year
        // does not (it completes the date's shape - a bare "23 Oct" reads
        // clipped). The year shows only for far dates in a different
        // calendar year (or always-show-year callers, thisYear -1).
        //
        // TIME is NOT horizon-limited. Reaching this function already means
        // times are wanted - the plan's show-time setting is a hard gate one
        // level up (GenericDateFormatter.formatDateTime), and semantic
        // callers declare alwaysShowTime. A horizon here would second-guess
        // an explicit user choice: on a multi-year plan it left ~80% of
        // tasks with no time even though the user had asked for times, with
        // no way to get them back (Crane, comparing real schedules). The
        // near/far distinction still governs the YEAR, where it answers a
        // real question ("which January?"). Every date carries 3 or 4 items,
        // never 2, never the busy 5.
        const { near, nowYear } = nearAndNowYear(when, now);
        const showYear = !options.hideYear
            && (thisYear === -1 || (!near && when.year() !== nowYear));
        const datePart = showYear
            ? (options.alwaysShowWeekday ? "ddd D MMM YYYY" : "D MMM YYYY")
            : "ddd D MMM";
        result = when.format(datePart + " h:mm a");
    }

    // remove any trailing :00. Replace the space too to bring the am/pm right next to it.
    if (!options.showZeroMinutes) {
        result = result.replace(':00 ', '');
        result = result.replace(':00', '');
    }

    return result;
}

export function formatEasyToReadDate(when: dayjs.Dayjs | null, thisYear: number = -1, relativeTo?: dayjs.Dayjs | null, hideYear?: boolean, now?: dayjs.Dayjs, alwaysShowWeekday?: boolean): string {
    if (when === null) return "";

    const nowValue = now || dayjs();

    if (relativeTo && relativeTo.year() === when.year() && relativeTo.month() === when.month() && relativeTo.date() === when.date()) {
        return "";
    }

    // Strict weekday XOR year: the weekday shows exactly when the year does
    // not (it completes the date's shape - a bare "23 Oct" reads clipped).
    // The year shows only for far dates in a different calendar year (or
    // always-show-year callers, thisYear -1). Never both, never neither.
    const { near, nowYear } = nearAndNowYear(when, nowValue);
    const showYear = !hideYear
        && (thisYear === -1 || (!near && when.year() !== nowYear));
    return showYear
        ? when.format(alwaysShowWeekday ? "ddd D MMM YYYY" : "D MMM YYYY")
        : when.format("ddd D MMM")
}

export function formatTime(when: dayjs.Dayjs): string {
    return when.format("h:mm a");
}


// js-joda's text patterns (EEE, MMM) need @js-joda/locale; this project doesn't
// bundle it, so we format day and month names from these tables instead.
const SHORT_DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SHORT_MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Weekday XOR year, same as the moment formatters above: the year replaces
// the weekday rather than joining it, so a date never carries both.
function formatJodaDateDDMMM(date: LocalDate, includeYear: boolean): string {
    const dow = SHORT_DAY_NAMES[date.dayOfWeek().value() - 1];
    const month = SHORT_MONTH_NAMES[date.monthValue() - 1];
    return includeYear
        ? `${date.dayOfMonth()} ${month} ${date.year()}`
        : `${dow} ${date.dayOfMonth()} ${month}`;
}

function isNearTodayLocal(when: LocalDate, now: LocalDate): boolean {
    return Math.abs(ChronoUnit.DAYS.between(now, when)) <= NEAR_DATE_HORIZON_DAYS;
}

function formatJodaTime12h(time: LocalTime): string {
    const h = time.hour();
    const hour12 = (h % 12) || 12;
    const ampm = h < 12 ? 'am' : 'pm';
    return `${hour12}:${String(time.minute()).padStart(2, '0')} ${ampm}`;
}

export function formatEasyToReadLocalDateTime(when: LocalDateTime, thisYear: number, options?: {
    relativeTo?: LocalDateTime | null,
    hideYear?: boolean,
    showZeroMinutes?: boolean,
    /** Injectable "today" for deterministic specs; defaults to now. */
    now?: LocalDate,
}): string {

    let result: string;

    options = options || {};

    const date = when.toLocalDate();
    const now = options.now || LocalDate.now();
    const timeStr = formatJodaTime12h(when.toLocalTime());

    if (options.relativeTo && options.relativeTo.toLocalDate().equals(date)) {
        result = timeStr;
    } else {
        // Same two-rule model as formatEasyToReadDateTime: the weekday and the
        // time are operational detail carried only inside the rolling horizon;
        // the year is disambiguation for a far date in another calendar year.
        const near = isNearTodayLocal(date, now);
        const showYear = !options.hideYear
            && (thisYear === -1 || (!near && when.year() !== now.year()));
        result = near
            ? `${formatJodaDateDDMMM(date, showYear)} ${timeStr}`
            : formatJodaDateDDMMM(date, showYear);
    }

    if (!options.showZeroMinutes) {
        result = result.replace(':00 ', '');
        result = result.replace(':00', '');
    }

    return result;
}

export function formatEasyToReadLocalDate(when: LocalDate | null, thisYear: number = -1, relativeTo?: LocalDate | null, hideYear?: boolean, now?: LocalDate): string {
    if (when === null) return "";

    if (relativeTo && relativeTo.equals(when)) {
        return "";
    }

    const nowValue = now || LocalDate.now();
    const near = isNearTodayLocal(when, nowValue);
    const showYear = !hideYear
        && (thisYear === -1 || (!near && when.year() !== nowValue.year()));
    return formatJodaDateDDMMM(when, showYear);
}

export function formatLocalTime(when: LocalTime | LocalDateTime): string {
    const time = when instanceof LocalDateTime ? when.toLocalTime() : when;
    return formatJodaTime12h(time);
}

export type DurationFormatStyle = 'terse' | 'short' | 'long';


//export function formatDurationRelativeToStartDate(start: dayjs.Dayjs, end: dayjs.Dayjs, maxPieces: number, formatStyle: DurationFormatStyle): string;
//export function formatDurationRelativeToStartDate(start: dayjs.Dayjs, end: dayjs.Dayjs, maxPieces: number, formatStyle: DurationFormatStyle): string;
export function formatDurationRelativeToStartDate(start: dayjs.Dayjs, end: dayjs.Dayjs, maxPieces: number, formatStyle: DurationFormatStyle): string {

    let { YEAR_UNIT, MONTH_UNIT, DAY_UNIT, HOUR_UNIT, MINUTE_UNIT, allowPlural } = getUnitsForFormatStyle(formatStyle);
    const units = [YEAR_UNIT, MONTH_UNIT, DAY_UNIT, HOUR_UNIT, MINUTE_UNIT];

    if (end.isBefore(start)) {
        const tempDate = start;
        start = end;
        end = tempDate;
    }

    let goAgainCount = 0;
    const MAX_GO_AGAIN_COUNT = 20;

    let counts = [0, 0, 0, 0, 0];
    TRY_AGAIN: while (goAgainCount < MAX_GO_AGAIN_COUNT) {
        if (start.isSame(end) || start.add(1, 'minute').isAfter(end)) {
            return `0${formatStyle === 'terse' ? '' : ' '}${units[3]}${allowPlural ? 's' : ''}`;
        }

        counts = getCountsOfTimeUnitsBetweenDateTimes(start, end);

        let firstNonZeroIndex = -1;
        let lastNonZeroIndex = -1;

        for (let i = 0; i < counts.length; ++i) {
            const value = counts[i];
            if (firstNonZeroIndex < 0 && value > 0) {
                firstNonZeroIndex = i;
            }
            if (value > 0) {
                lastNonZeroIndex = i;
            }
        }

        const numberOfUsedParts = lastNonZeroIndex - firstNonZeroIndex + 1;
        if (numberOfUsedParts > maxPieces) {
            const numberOfPartsToZeroOut = numberOfUsedParts - maxPieces;
            for (let i = 0; i < numberOfPartsToZeroOut; ++i) {
                const indexToZeroOut = lastNonZeroIndex - i;
                if (counts[indexToZeroOut] > 0) {
                    counts[indexToZeroOut - 1]++;
                    counts[indexToZeroOut] = 0;

                    end = start
                        .add(counts[0], 'year')
                        .add(counts[1], 'month')
                        .add(counts[2], 'day')
                        .add(counts[3], 'hour')
                        .add(counts[4], 'minute');

                    if (++goAgainCount < MAX_GO_AGAIN_COUNT) {
                        continue TRY_AGAIN;
                    }
                }
            }
        }

        break;
    }

    let result = "";

    // Terse is the COMPACT family (cells, cards, tooltips): the unit attaches
    // to its number, "1mo 11d". Every other style is the WORDS family
    // (labeled panel rows), which keeps its space and plurals.
    const attached = formatStyle === 'terse';

    for (let i = 0; i < counts.length; ++i) {
        const value = counts[i];
        const unit = units[i];
        if (value > 0) {
            result += `${value}${attached ? '' : ' '}${unit}${allowPlural && value !== 1 ? 's' : ''} `;
        }
    }

    return result.trim();
}

//export function getCountsOfTimeUnitsBetweenDateTimes(start: dayjs.Dayjs, end: dayjs.Dayjs): number[];
//export function getCountsOfTimeUnitsBetweenDateTimes(start: dayjs.Dayjs, end: dayjs.Dayjs): number[];
export function getCountsOfTimeUnitsBetweenDateTimes(start: dayjs.Dayjs, end: dayjs.Dayjs): number[] {

    let adjustedStart = start;

    let years = 0;
    while (isAtOrBefore(adjustedStart.add(years + 1, 'year'), end)) {
        years++;
    }
    adjustedStart = adjustedStart.add(years, 'year');

    let months = 0;
    // It is critical that we do not incrementally add one month at a time to the
    // adjustedStart variable. This can change behaviour significantly with different duration months
    // i.e. date+6months is not guranteed to be the same as date+1+1+1+1+1+1+1months
    while (isAtOrBefore(adjustedStart.add(months + 1, 'month'), end)) {
        months++;
    }
    adjustedStart = adjustedStart.add(months, 'month')

    let days = 0;
    while (isAtOrBefore(adjustedStart.add(days + 1, 'day'), end)) {
        days++;
    }
    adjustedStart = adjustedStart.add(days, 'day');

    let hours = 0;
    while (isAtOrBefore(adjustedStart.add(hours + 1, 'hour'), end)) {
        hours++;
    }
    adjustedStart = adjustedStart.add(hours, 'hour');

    // the only loop stepping one unit at a time, so it carries the next value
    // forward instead of building it twice per iteration
    let minutes = 0;
    let nextMinute = adjustedStart.add(1, 'minute');
    while (isAtOrBefore(nextMinute, end)) {
        adjustedStart = nextMinute;
        nextMinute = adjustedStart.add(1, 'minute');
        minutes++;
    }

    return [years, months, days, hours, minutes];
}

export function getUnitsForFormatStyle(formatStyle: DurationFormatStyle) {
    let YEAR_UNIT: string;
    let MONTH_UNIT: string;
    let DAY_UNIT: string;
    let HOUR_UNIT: string;
    let MINUTE_UNIT: string;
    let allowPlural: boolean;

    switch (formatStyle) {
        case 'terse':
            YEAR_UNIT = 'y';
            // 'mo': 'mon' reads as one Monday and 'mth' is unpronounceable.
            MONTH_UNIT = 'mo';
            DAY_UNIT = 'd';
            HOUR_UNIT = 'h';
            MINUTE_UNIT = 'm';
            allowPlural = false;
            break;
        case 'short':
            YEAR_UNIT = 'yr';
            MONTH_UNIT = 'mth';
            DAY_UNIT = 'day';
            HOUR_UNIT = 'hr';
            MINUTE_UNIT = 'min';
            allowPlural = true;
            break;
        default:
            YEAR_UNIT = 'year';
            MONTH_UNIT = 'month';
            DAY_UNIT = 'day';
            HOUR_UNIT = 'hour';
            MINUTE_UNIT = 'minute';
            allowPlural = true;
            break;
    }
    return { YEAR_UNIT, MONTH_UNIT, HOUR_UNIT, DAY_UNIT, MINUTE_UNIT, allowPlural, };
}

export class GenericDateFormatter implements IDateFormatter {
    private thisYear: number;

    constructor(
        private planDurationDays: () => number,
        private getHoursPerDay: () => number,
        /**
         * The plan's "Show time on Whiteboard and Gantt" setting, and a HARD
         * GATE on formatDateTime: when false, no scheduled date rendered
         * through this formatter carries a time, with no per-surface
         * exceptions - many users (construction especially) schedule only in
         * days, and a stray "4am" is clutter they cannot turn off. Recorded
         * actuals opt out via DateOptions.alwaysShowTime.
         *
         * Deliberately PRIVATE and absent from IDateFormatter: it is an
         * input to rendering, not something callers branch on. Surfaces that
         * need the same granularity for a COMPARISON take a
         * DateTimeComparisonOptions built from the plan's settings instead
         * (see dateComparisonOptionsForSettings), which also carries the
         * timezone the comparison must happen in.
         *
         * Defaults to showing times so the standalone/fallback formatters
         * built without a plan behave exactly as before.
         */
        private showTimeInDates: () => boolean = () => true,
    ) {
        this.thisYear = dayjs().year();
    }

    // The old plan-duration rule (short plans never show the year) is gone:
    // the rolling near/far horizon inside formatEasyToReadDate[Time] handles
    // every case - near dates need no year regardless of plan length, and a
    // far date in another year showing its year is MORE correct on a short
    // Dec-Feb plan than the old always-hide. planDurationDays stays on the
    // constructor for callers but no longer influences date rendering.
    formatDateTime(when: dayjs.Dayjs, options?: DateOptions): string {
        let hideYear = false;
        let year = this.thisYear;

        if (options && options.yearOptions === YearOptions.NeverShowYear) hideYear = true;
        if (options && options.yearOptions === YearOptions.AlwaysShowYear) year = -1;

        // THE GATE: a plan scheduled in days renders no times at all, whatever
        // the surface asked for. Recorded actuals (timesheets, set-complete,
        // message stamps) declare alwaysShowTime and are exempt - their times
        // are the content, not scheduling detail.
        if (!this.showTimeInDates() && !(options && options.alwaysShowTime)) {
            // relativeTo is deliberately DROPPED here. Its job is "same day as
            // the reference, so show only the time" - formatEasyToReadDate
            // answers that with an empty string, which is right for a date
            // renderer and catastrophic here: the whiteboard tooltip builds
            // "start -> end" and hides itself entirely when either half is
            // blank, so every same-day task lost its tooltip.
            return formatEasyToReadDate(when, year, null, hideYear, undefined,
                options ? options.alwaysShowWeekday : undefined)
        }

        return formatEasyToReadDateTime(when, year, {
            relativeTo: options ? options.relativeTo : null,
            hideYear,
            alwaysShowTime: options ? options.alwaysShowTime : undefined,
            alwaysShowWeekday: options ? options.alwaysShowWeekday : undefined,
        })
    }

    formatDate(when: dayjs.Dayjs, options?: DateOptions): string {
        let hideYear = false;
        let year = this.thisYear;

        if (options && options.yearOptions === YearOptions.NeverShowYear) hideYear = true;
        if (options && options.yearOptions === YearOptions.AlwaysShowYear) year = -1;

        return formatEasyToReadDate(when, year, options ? options.relativeTo : null, hideYear, undefined, options ? options.alwaysShowWeekday : undefined)
    }

    formatDateShort(when: dayjs.Dayjs): string {
        return when.format("D MMM YY")
    }

    formatTime(when: dayjs.Dayjs): string {
        return formatTime(when)
    }

    formatLocalDateTime(when: LocalDateTime, options?: DateOptions): string {
        let hideYear = false;
        let year = this.thisYear;

        if (options && options.yearOptions === YearOptions.NeverShowYear) hideYear = true;
        if (options && options.yearOptions === YearOptions.AlwaysShowYear) year = -1;
        throw new Error("not implemented")
        // return formatEasyToReadLocalDateTime(when, year, { relativeTo: options ? options.relativeTo : null, hideYear })
    }

    formatLocalDate(when: LocalDate, options?: DateOptions): string {
        let hideYear = false;
        let year = this.thisYear;

        if (options && options.yearOptions === YearOptions.NeverShowYear) hideYear = true;
        if (options && options.yearOptions === YearOptions.AlwaysShowYear) year = -1;
        throw new Error("not implemented")
        // return formatEasyToReadLocalDate(when, year, options ? options.relativeTo : null, hideYear)
    }

    formatLocalTime(when: LocalTime, options?: DateOptions): string {
        throw new Error("not implemented")
        // return formatTimeLocal(when)
    }

    formatDurationToDaysAndHoursRelativeToNothing(hours: number): string {
        return formatDurationToDaysAndHoursRelativeToNothing(hours, this.getHoursPerDay());

    }

    formatDurationRelativeToStartDate(start: dayjs.Dayjs, end: dayjs.Dayjs, durationFormatStyle: DurationFormatStyle): string {
        // Elapsed spans honour the show-time gate: whole days only, with the
        // working-day promotion rule (see roundSpanEndToWholeDays).
        const gatedEnd = this.showTimeInDates()
            ? end
            : roundSpanEndToWholeDays(start, end, this.getHoursPerDay());
        return formatDurationRelativeToStartDate(start, gatedEnd, 3, durationFormatStyle);
    }


    formatHours(hours: number, durationFormatStyle: DurationFormatStyle): string {
        return formatDurationSecondsToHoursRelativeToNothing(hours * 60 * 60, durationFormatStyle)
    }
}

/**
 * Rounds a span's end to whole days from its start, for elapsed times behind
 * the plan's show-time gate: an hours tail is sub-day precision the same way
 * "4am" is. The remainder counts as one more day when it is at least the
 * plan's WORKING day - a 2-day task's elapsed "1 day 8 hrs" at 8h/day IS two
 * days to a day-mode user, and truncating it to "1 day" would torch
 * confidence in every other number (Crane, 2026-08-19) - and truncates
 * otherwise, the way dates drop their times. Spans under a day are returned
 * untouched: there the hours are the content. A remainder can only ever add
 * one day, so nothing inflates.
 */
export function roundSpanEndToWholeDays(start: dayjs.Dayjs, end: dayjs.Dayjs, hoursPerDay: number): dayjs.Dayjs {
    const totalMinutes = end.diff(start, 'minutes');
    if (totalMinutes < 24 * 60) {
        return end;
    }

    const wholeDays = Math.floor(totalMinutes / (24 * 60));
    const remainderMinutes = totalMinutes - wholeDays * 24 * 60;
    const promote = remainderMinutes >= (hoursPerDay || 8) * 60;

    // Calendar-aware day stepping, so month rollups in the decomposition
    // stay honest across DST and month boundaries.
    return start.add(wholeDays + (promote ? 1 : 0), 'day');
}

export function formatDateRangeToMinutesRelativeToNothing(a: dayjs.Dayjs, b: dayjs.Dayjs): string {
    const seconds = b.diff(a, 'seconds');
    return formatDurationToMinutesRelativeToNothing(seconds);
}

export function formatDurationToDaysAndHoursRelativeToNothing(totalHours: number, hoursPerDay: number): string {

    let totalMinutes = totalHours * 60;
    let minutesPerDay = hoursPerDay * 60;
    let wholeDays = 0;
    let wholeHours = 0;
    let wholeMinutes = 0;

    if (totalMinutes >= minutesPerDay) {
        wholeDays = Math.floor(totalMinutes / minutesPerDay);
        totalMinutes -= wholeDays * minutesPerDay;
    }

    if (totalMinutes >= 60) {
        wholeHours = Math.floor(totalMinutes / 60);
        totalMinutes -= wholeHours * 60;
    }

    if (totalMinutes >= 1) {
        wholeMinutes = Math.floor(totalMinutes);
        totalMinutes -= wholeMinutes;
    }

    // because we'll be rounding to a single decimal place, 57 minutes or more will round up to 1.0
    // so lets adjust for that here
    if (wholeMinutes >= 57) {
        wholeHours += 1;
        wholeMinutes = 0;
        if (wholeHours >= 24) {
            wholeHours = 0;
            wholeDays += 1;
        }
    }

    let result = "";
    if (wholeHours > 0 || wholeMinutes > 0) {
        result += wholeHours;
        if (wholeMinutes === 15) result += ".25";
        else if (wholeMinutes === 30) result += ".5";
        else if (wholeMinutes === 45) result += ".75";
        else if (wholeMinutes > 0) result += `.${Math.round(10 * wholeMinutes / 60)}`;
        result += "h";
    }

    if (wholeDays > 0) {
        result = `${wholeDays}d ${result}`;
    }

    return result
}

export function formatDurationToMinutesRelativeToNothing(seconds: number): string {
    let secondsRounded = Math.round(seconds / 60) * 60;
    return juration.stringify(secondsRounded);
}

export function formatDurationSecondsToHoursRelativeToNothing(seconds: number, durationStyle: DurationFormatStyle): string {

    const roundingFactor = durationStyle === 'terse' ? 10 : 100;

    const { HOUR_UNIT, allowPlural } = getUnitsForFormatStyle(durationStyle);
    const gap = durationStyle === 'terse' ? '' : ' ';
    let secondsRounded = Math.round(seconds / 60) * 60;
    let hours = secondsRounded / 3600;
    hours = Math.round(hours * roundingFactor) / roundingFactor;

    // Plurality follows the RENDERED number, not the raw one: 57 to 62
    // minutes rounds to "1" for display but is never === 1 here, and used to
    // render "1 hrs". No trailing space (the 1-hour branch never had one).
    const rendered = intlNumberFormatOneDecimals.format(hours);
    const plural = allowPlural && rendered !== '1';
    return `${rendered}${gap}${HOUR_UNIT}${plural ? 's' : ''}`;
}



